import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { userService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import Pagination from '../components/Pagination';
import LevelBadge from '../components/LevelBadge';
import UserCardModal from '../components/UserCardModal';
import DirectoryEditModal from '../components/DirectoryEditModal';
import { formatDate, getDisplayName } from '../utils/helpers';
import './UserDirectoryPage.css';

const UserDirectoryPage = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canManageDirectory = hasPermission('directory.manage');
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 12, pages: 1 });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await userService.list({ page, limit: 12, search });
        setUsers(res.data?.items || []);
        setPagination(res.data?.pagination || { total: 0, page, limit: 12, pages: 1 });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [page, search, reloadKey]);

  const handleSearch = (e) => {
    e.preventDefault();
    // 業務邏輯：搜尋送出後才查詢，避免使用者每打一個字就打 API 造成不必要流量。
    setPage(1);
    setSearch(searchInput.trim());
  };

  if (loading) return <div className="container mt-5">載入中...</div>;

  return (
    <div className="user-directory-page">
      <div className="container">
        <div className="directory-header">
          <div>
            <h1>人員名錄</h1>
            <p>搜尋夥伴的公開資料、聯絡方式與等級資訊。</p>
          </div>
          <form className="directory-search" onSubmit={handleSearch}>
            <input className="form-control" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="搜尋暱稱、姓名、帳號或 LINE" />
            <button className="btn btn-primary" type="submit">搜尋</button>
          </form>
        </div>

        <div className="directory-toolbar">
          <span>顯示模式</span>
          <div className="btn-group">
            <button className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('grid')}>Grid View</button>
            <button className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('list')}>List View</button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {users.length === 0 ? (
          <div className="directory-empty">查無符合條件的人員</div>
        ) : (
          viewMode === 'grid' ? (
            <div className="directory-grid">
              {users.map((person) => {
                const displayName = getDisplayName(person);
                return (
                  <article className="directory-card" key={person.id}>
                    <button className="directory-avatar-button" onClick={() => setSelectedUser(person)} title="查看 Profile Card">
                      {person.avatar_url ? <img className="directory-avatar-img" src={person.avatar_url} alt={displayName} /> : <div className="directory-avatar">{displayName.charAt(0).toUpperCase()}</div>}
                    </button>
                    <div className="directory-card-body">
                      <h3>{displayName}</h3>
                      {person.nickname && <p className="directory-real-name">{person.name}</p>}
                      <LevelBadge userId={person.id} compact />
                      <div className="directory-info"><span>入職日</span><strong>{person.joined_at ? formatDate(person.joined_at) : '-'}</strong></div>
                      <div className="directory-info"><span>聯絡方式</span><strong>{person.contact_info || '-'}</strong></div>
                      <div className="directory-info"><span>LINE</span><strong>{person.line_id || '-'}</strong></div>
                      <div className="directory-card-actions">
                        {canManageDirectory && <button className="btn btn-sm btn-secondary" onClick={() => setEditingPerson(person)}>編輯名錄</button>}
                        {user?.role === 'ADMIN' && <Link className="btn btn-sm btn-secondary" to={`/admin?tab=users&editUser=${person.id}`}>編輯用戶</Link>}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <table className="directory-table">
              <thead><tr><th>頭像</th><th>姓名</th><th>等級</th><th>入職日</th><th>聯絡方式</th><th>LINE</th><th>操作</th></tr></thead>
              <tbody>
                {users.map((person) => {
                  const displayName = getDisplayName(person);
                  return (
                    <tr key={person.id}>
                      <td><button className="directory-avatar-button" onClick={() => setSelectedUser(person)}>{person.avatar_url ? <img className="directory-avatar-img small" src={person.avatar_url} alt={displayName} /> : <div className="directory-avatar small">{displayName.charAt(0).toUpperCase()}</div>}</button></td>
                      <td><strong>{displayName}</strong>{person.nickname && <div className="directory-real-name">{person.name}</div>}</td>
                      <td><LevelBadge userId={person.id} compact /></td>
                      <td>{person.joined_at ? formatDate(person.joined_at) : '-'}</td>
                      <td>{person.contact_info || '-'}</td>
                      <td>{person.line_id || '-'}</td>
                      <td>
                        <div className="btn-group">
                          {canManageDirectory && <button className="btn btn-sm btn-secondary" onClick={() => setEditingPerson(person)}>編輯名錄</button>}
                          {user?.role === 'ADMIN' && <Link className="btn btn-sm btn-secondary" to={`/admin?tab=users&editUser=${person.id}`}>編輯用戶</Link>}
                          {!canManageDirectory && user?.role !== 'ADMIN' && '-'}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        <Pagination pagination={pagination} currentPage={page} onPageChange={setPage} unitLabel="位人員" />
      </div>
      <UserCardModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      {editingPerson && (
        <DirectoryEditModal
          person={editingPerson}
          onClose={() => setEditingPerson(null)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
};

export default UserDirectoryPage;
